import React, { useEffect, useState } from "react";
import { t } from "i18next";
import s from "./mtmRequests.scss";
import QboxmailApi from "@/qboxmailApi";
import Loader from "Components/loader/Loader";
import Button from "Components/button/Button";
import DomainStatusBadge from "Components/domainStatusBadge/DomainStatusBadge";
import useWindowFocus from "use-window-focus";
import { view } from "@risingstack/react-easy-state";
import sessionStore from "@/stores/session";

const api = new QboxmailApi({ useCookie: true });

const MtmRequestsPage = (props) => {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [errored, setErrored] = useState(false);
  const [loadingUpdates, setLoadingUpdates] = useState({});
  const [activeTimer, setActiveTimer] = useState(null);

  const windowFocused = useWindowFocus();

  useEffect(() => {
    if (!windowFocused) return;
    getRequests();
  }, [windowFocused]);

  useEffect(() => {
    setLoading(true);
    getRequests().then(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTimer) {
      window.clearTimeout(activeTimer);
      setActiveTimer(null);
    }

    const running = requests.find((r) => r.operation_status === "running");

    if (running) {
      setActiveTimer(setTimeout(getRequests, 5000));
    }
  }, [requests]);

  const getRequests = async () => {
    const pendingRequests = await api.getMTMRequestes().catch((err) => {
      console.log("Error while getting pending MTM requests:");
      console.log(err);
      setErrored(true);
      return null;
    });

    if (!pendingRequests) return;

    setRequests(pendingRequests.resources);
  };

  const updateStatus = async (id, action) => {
    setLoadingUpdates({ ...loadingUpdates, [id]: true });

    const resp = await api.updateMTMRequest(id, { request_status: action });
    await getRequests();

    sessionStore.checkPendingMTMRequests();

    setLoadingUpdates({ ...loadingUpdates, [id]: false });
  };

  if (errored) {
    return (
      <div
        className={s.loader}
        style={{ paddingTop: "5rem", display: "flex", justifyContent: "center", textAlign: "center" }}
      >
        Ops. C'è stato un errore. Contatta il supporto.
      </div>
    );
  }

  if (loading) {
    return (
      <div className={s.loader} style={{ paddingTop: "8rem" }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className="sectionSP">
        <div className="infoSectionSP">
          <div className="settingTitle">{t("mtm.sectionTitle")}</div>

          <div
            className="sectionDesc"
            dangerouslySetInnerHTML={{
              __html: t("mtm.sectionDesc"),
            }}
          ></div>
        </div>
        <div className={s.content}>
          <div className={s.docsBtnContainer}>
            <Button docsKey="mtmRestores" color="docs" className={s.btnList}>
              button.docs
            </Button>
          </div>

          {requests.length === 0 && <div className={s.noRequests}>{t("mtm.noRequests")}</div>}

          {requests.map((r) => (
            <div className={s.request} key={r.id}>
              <div className={s.header}>
                {t("mtm.cardHeader", {
                  date: new Date(r.requested_at).toLocaleString(),
                })}
              </div>

              <div className={s.body}>
                <div className={s.meta}>
                  <div>
                    <label>{t("mtm.cardMailLabel")}:</label> {r.email_account}
                  </div>
                  <div>
                    <label>{t("mtm.cardDayLabel")}:</label> {new Date(r.requested_day).toLocaleDateString()}
                  </div>
                </div>

                {r.expired ? (
                  <div className={s.expirationDate}>
                    <label>{t("mtm.expired")}</label>
                  </div>
                ) : (
                  <div className={s.expirationDate}>
                    <label>{t("mtm.expiresAtLabel")}:</label> {new Date(r.expires_at).toLocaleDateString()}
                  </div>
                )}

                <div className={s.footer}>
                  {r.request_status === "pending" && !r.expired && (
                    <>
                      <Button
                        type="button"
                        color="red-outline"
                        onClick={() => updateStatus(r.id, "denied")}
                        loading={loadingUpdates[r.id]}
                      >
                        button.mtmRestore.deny
                      </Button>

                      <Button
                        type="button"
                        color="accent"
                        onClick={() => updateStatus(r.id, "accepted")}
                        loading={loadingUpdates[r.id]}
                      >
                        button.mtmRestore.accept
                      </Button>
                    </>
                  )}

                  {r.request_status === "denied" && (
                    <>
                      <div className={s.denied}>{t("mtm.denied")}</div>
                    </>
                  )}

                  {r.request_status === "accepted" && r.operation_status === "running" && (
                    <div className={s.runningMsg}>
                      <DomainStatusBadge status="updating" />
                      <div className={s.accepted}>{t("mtm.running")}</div>
                    </div>
                  )}

                  {r.operation_status === "completed" && (
                    <>
                      <div className={s.completed}>{t("mtm.completed")}</div>
                    </>
                  )}

                  {r.operation_status === "error" && (
                    <>
                      <div className={s.denied}>{t("mtm.errored")}</div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default view(MtmRequestsPage);
